import { Link, useParams } from '@tanstack/react-router'
import { ArrowRight, Mail, MapPin, Phone } from 'lucide-react'
import { Suspense, lazy, useEffect, useState } from 'react'
import { useTranslations } from 'use-intl'

import { BookDemoButton } from '@/components/cta-buttons'
import { LinkedinIcon } from '@/components/icons/linkedin'
import { Logo } from '@/components/ui/logo'
import { LocaleSwitcher } from '@/components/locale-switcher'
import { SITE } from '@/lib/site-config'
import { SOLUTIONS } from '@/lib/solutions'
import { TOOLS } from '@/lib/tools'
import { APP_NAMES } from '@/features/solution-page/data/app-showcase'
import { FEATURE_GROUPS } from '@/features/solution-page/data/features'
import { trackNavClick, trackOutboundClick } from '@/lib/analytics/events'

// Newsletter form is footer content (below the fold). Client-only lazy mount so
// its heavy deps (disposable-email-domains ~2.4MB + react-hook-form + zod) leave
// the shared /$locale layout chunk and never load during /fr SSR. The server
// still validates on submit (src/server/newsletter.ts), so this is purely a
// client bundle move.
const LazyNewsletterForm = lazy(() =>
  import('@/components/newsletter-form').then((m) => ({
    default: m.NewsletterForm,
  })),
)

// Placeholder matches the loaded card (378px; 410px on desktop fr, where the
// title wraps once more) so the about card stacked below barely moves when the
// form mounts.
function NewsletterFormLazy() {
  // mounted=false on the server AND on the first client render (matches SSR,
  // no hydration mismatch); flips true after hydration to trigger the import.
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  if (!mounted) return <div className="min-h-[378px]" aria-hidden />
  return (
    <Suspense fallback={<div className="min-h-[378px]" aria-hidden />}>
      <LazyNewsletterForm />
    </Suspense>
  )
}

export type FooterPost = { title: string; slug: string }

// TOOLS slug → existing nav.toolsItems.* label key (marketing labels reused from the header).
const TOOL_LABEL_KEY: Record<string, string> = {
  'security-audit-alerts': 'maturity',
  'ppms-audit': 'ppms',
  'compliance-audit': 'compliance',
  'solution-configurator': 'solution',
}

function FrenchFlag() {
  return (
    <span className="inline-block w-fit border border-footer-muted leading-none">
      <svg
        width="160"
        height="7"
        viewBox="0 0 160 7"
        aria-hidden
        className="block"
      >
        <rect width="53.33" height="7" fill="#0055A4" />
        <rect x="53.33" width="53.33" height="7" fill="#fff" />
        <rect x="106.66" width="53.34" height="7" fill="#EF4135" />
      </svg>
    </span>
  )
}

// ponytail: lucide-react dropped brand icons; inline SVG avoids a new dependency for the Facebook glyph.
function Facebook({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
      className={className}
    >
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  )
}

export function Footer({ posts }: { posts: FooterPost[] }) {
  const t = useTranslations()
  const { locale } = useParams({ from: '/$locale' })

  return (
    <footer className="bg-footer-from text-footer-foreground">
      <div className="mx-auto max-w-7xl px-6 pt-20 pb-12 lg:px-8">
        <Logo size="md" alt="CountAct" className="mb-10 brightness-0 invert" />

        {/* Last column at 1.5fr: the newsletter form needs more width than
            the link lists, otherwise its title wraps onto 4 lines. */}
        <div className="grid gap-8 lg:grid-cols-[1fr_1fr_1fr_1fr_1.5fr] lg:gap-4">
          {/* Col 1 — Solutions */}
          <div>
            <h2 className="mb-4 font-semibold text-footer-foreground">
              {t('footer.solutions')}
            </h2>
            <ul className="space-y-2 text-footer-muted">
              <li>
                <Link
                  to={SOLUTIONS.property.to as never}
                  params={{ locale } as never}
                  onClick={() =>
                    trackNavClick({
                      destination: SOLUTIONS.property.to,
                      source: 'footer',
                    })
                  }
                  className="hover:text-footer-foreground"
                >
                  {t('nav.solutionsItems.property')}
                </Link>
              </li>
              <li>
                <Link
                  to={SOLUTIONS.people.to as never}
                  params={{ locale } as never}
                  onClick={() =>
                    trackNavClick({
                      destination: SOLUTIONS.people.to,
                      source: 'footer',
                    })
                  }
                  className="hover:text-footer-foreground"
                >
                  {t('nav.solutionsItems.people')}
                </Link>
              </li>
            </ul>

            <h2 className="mt-8 mb-4 font-semibold text-footer-foreground">
              {t('footer.features')}
            </h2>
            <div className="space-y-4">
              {FEATURE_GROUPS.map((group) => (
                <div key={group.key}>
                  <p className="mb-2 text-sm font-medium text-footer-foreground/80">
                    {t(`nav.solutionsItems.${group.key}`)}
                  </p>
                  <ul className="space-y-2 text-footer-muted">
                    {group.features.map((feature) => (
                      <li key={feature}>
                        <Link
                          to={group.to as never}
                          params={{ locale, feature } as never}
                          onClick={() =>
                            trackNavClick({
                              destination: group.to,
                              source: 'footer',
                            })
                          }
                          className="hover:text-footer-foreground"
                        >
                          {APP_NAMES[feature]}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Col 2 — Free tools */}
          <div>
            <h2 className="mb-4 font-semibold text-footer-foreground">
              {t('nav.freeTools')}
            </h2>
            <ul className="space-y-2 text-footer-muted">
              {TOOLS.map((tool) => (
                <li key={tool.slug}>
                  <Link
                    to={`/$locale${tool.href}` as never}
                    params={{ locale } as never}
                    onClick={() =>
                      trackNavClick({
                        destination: tool.href,
                        source: 'footer',
                      })
                    }
                    className="flex items-center gap-2 hover:text-footer-foreground"
                  >
                    <ArrowRight className="size-4 shrink-0" />
                    {t(`nav.toolsItems.${TOOL_LABEL_KEY[tool.slug]}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3 — Articles */}
          <div>
            <h2 className="mb-4 font-semibold text-footer-foreground">
              {t('footer.articles')}
            </h2>
            <ul className="space-y-2 text-footer-muted">
              {posts.map((post) => (
                <li key={post.slug}>
                  <Link
                    to={'/$locale/blog/$slug' as never}
                    params={{ locale, slug: post.slug } as never}
                    onClick={() =>
                      trackNavClick({
                        destination: '/$locale/blog/$slug',
                        source: 'footer',
                      })
                    }
                    className="hover:text-footer-foreground"
                  >
                    {post.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4 — CTA + contact */}
          <div className="space-y-4 text-footer-muted">
            <BookDemoButton source="footer" className="w-full">
              {t('footer.bookDemo')}
            </BookDemoButton>
            <p className="flex items-center gap-2">
              <MapPin className="size-4 shrink-0" />
              {SITE.location}
            </p>
            <a
              href={SITE.phoneHref}
              onClick={() =>
                trackOutboundClick({
                  pageType: 'home',
                  destination: SITE.phoneHref,
                })
              }
              className="flex items-center gap-2 hover:text-footer-foreground"
            >
              <Phone className="size-4 shrink-0" />
              {SITE.phone}
            </a>
            <a
              href={SITE.emailHref}
              onClick={() =>
                trackOutboundClick({
                  pageType: 'home',
                  destination: SITE.emailHref,
                })
              }
              className="flex items-center gap-2 hover:text-footer-foreground"
            >
              <Mail className="size-4 shrink-0" />
              {SITE.email}
            </a>
          </div>

          {/* Col 5 — Newsletter + about. Stacked in one flex column so the grid
              stretches the column, not the newsletter card. */}
          <div className="flex flex-col gap-4">
            <NewsletterFormLazy />
            <div className="rounded-lg border border-footer-muted p-6">
              <h3 className="mb-2 text-2xl font-semibold text-footer-foreground">
                {t('nav.whoWeAre')}
              </h3>
              <p className="mb-6 text-footer-muted">{t('footer.aboutText')}</p>
              <Link
                to={'/$locale/about' as never}
                params={{ locale } as never}
                onClick={() =>
                  trackNavClick({
                    destination: '/$locale/about',
                    source: 'footer',
                  })
                }
                className="flex h-12 w-full items-center justify-center rounded-lg bg-white text-base font-semibold text-primary transition-colors hover:bg-white/90"
              >
                {t('footer.aboutCta')}
              </Link>
            </div>
          </div>
        </div>

        {/* Flag + socials */}
        <div className="mt-12 space-y-8">
          <FrenchFlag />
          <div className="flex items-center gap-4 text-footer-muted">
            <a
              href={SITE.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              onClick={() =>
                trackOutboundClick({
                  pageType: 'home',
                  destination: SITE.facebook,
                })
              }
              className="hover:text-footer-foreground"
            >
              <Facebook className="size-5" />
            </a>
            <a
              href={SITE.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              onClick={() =>
                trackOutboundClick({
                  pageType: 'home',
                  destination: SITE.linkedin,
                })
              }
              className="hover:text-footer-foreground"
            >
              <LinkedinIcon className="size-5" />
            </a>
            <LocaleSwitcher className="-ml-2 hover:bg-white/10 hover:text-footer-foreground" />
          </div>
        </div>

        {/* Watermark */}
        <div
          aria-hidden
          className="mt-8 h-32 w-full overflow-hidden lg:h-48"
          style={{
            WebkitMaskImage: 'url(/wordmark.png)',
            maskImage: 'url(/wordmark.png)',
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
            WebkitMaskSize: '105% auto',
            maskSize: '105% auto',
            WebkitMaskPosition: 'center',
            maskPosition: 'center',
            background:
              'linear-gradient(90deg, rgba(47,64,255,0.02) 0%, rgba(47,64,255,0.45) 50%, rgba(47,64,255,0.02) 100%)',
          }}
        />
      </div>

      {/* Legal bar */}
      <div className="border-t border-footer-muted">
        <div className="mx-auto flex max-w-7xl flex-col gap-2 px-6 py-10 text-sm text-footer-muted sm:flex-row sm:justify-between lg:px-8">
          <nav className="flex gap-6">
            <Link
              to={'/$locale/legal/$slug' as never}
              params={{ locale, slug: 'politique-de-confidentialite' } as never}
              onClick={() =>
                trackNavClick({
                  destination: '/$locale/legal/politique-de-confidentialite',
                  source: 'footer',
                })
              }
              className="hover:text-footer-foreground"
            >
              {t('footer.privacy')}
            </Link>
            <Link
              to={'/$locale/legal/$slug' as never}
              params={{ locale, slug: 'mentions-legales' } as never}
              onClick={() =>
                trackNavClick({
                  destination: '/$locale/legal/mentions-legales',
                  source: 'footer',
                })
              }
              className="hover:text-footer-foreground"
            >
              {t('footer.legal')}
            </Link>
            {/* Cookie settings entry point — re-enable together with <CookieConsent />.
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event('open-cookie-settings'))}
              className="text-left hover:text-footer-foreground"
            >
              {t('footer.cookies')}
            </button> */}
          </nav>
          <p>{t('footer.copyright')}</p>
        </div>
      </div>
    </footer>
  )
}
